import { useEffect, useState } from "react";
import { api, fileUrl } from "../api";
import AuthImage from "./AuthImage";
import ZoomableLightbox from "./ZoomableLightbox";
import { formatDate } from "../lib/formatDate";
import ModalBackdrop from "./ui/ModalBackdrop";

// Date-repair modal. Scans every record on mount, surfaces ones
// whose stored date disagrees with the date found in the body text
// (forwarded-email filing-date bug), and lets the user apply
// individually or in bulk. No records are touched until the user
// explicitly clicks Apply.

interface Suggestion {
  record_id: string;
  current_date: string;
  suggested_date: string;
  subject: string | null;
  preview: string;
  file_r2_key: string | null;
  file_mime_type: string | null;
  confidence: "normal" | "unlikely";
  confidence_reason: string | null;
}

interface Props {
  caseId: string;
  onClose: () => void;
  onApplied: (count: number) => void;
  // Optional "View record" handler. When wired, each row grows a
  // "View record ↗" button that closes the modal, switches to the
  // Records tab, and scrolls to the record. The user can reopen
  // Repair dates from the Maintain menu to continue triaging.
  onViewRecord?: (recordId: string) => void;
}

// Delegate to the shared UTC-safe formatter. Retained as a named
// helper so the busy call sites below read naturally.
function formatDateDisplay(iso: string): string {
  return formatDate(iso, "short") || iso;
}

export default function RepairDatesModal({
  caseId,
  onClose,
  onApplied,
  onViewRecord,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [scanned, setScanned] = useState(0);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // record_ids the user has ticked off (skipped). Skipped records
  // stay visible but dimmed so the user sees what they've decided
  // against.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // record_ids that have been successfully applied. The row stays
  // in the list but shows "Applied ✓" and disables its buttons so
  // the user can triage the remaining suggestions without losing
  // context or scroll position. Parent is notified so it can
  // schedule a records refresh on modal close.
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When set, render a full-size auth-fetched lightbox over the
  // modal so the user can scrutinize the photo (e.g. a check date
  // vs the envelope date) without leaving the repair flow.
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.repairDatesScan(caseId);
        if (cancelled) return;
        setScanned(r.scanned);
        setSuggestions(r.suggestions);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      // The zoomable lightbox registers its own Escape listener in
      // the capture phase and calls stopPropagation, so when the
      // lightbox is open this listener never fires. Belt-and-braces
      // check anyway — if the lightbox is up and its listener
      // somehow didn't intercept, we still refuse to close the
      // whole modal.
      if (lightboxUrl) return;
      onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, lightboxUrl]);

  async function apply(repairs: Array<{ record_id: string; new_date: string }>) {
    if (repairs.length === 0) return;
    setApplying(true);
    setError(null);
    try {
      const r = await api.applyDateRepairs(caseId, repairs);
      // Tag the rows we just applied so they render as "Applied ✓"
      // inline. The modal stays open — the user keeps working
      // through the list. Parent is notified so it can bump a
      // running total (shown on close) and queue the records
      // refresh, but does NOT close the modal.
      setApplied((prev) => {
        const next = new Set(prev);
        for (const r of repairs) next.add(r.record_id);
        return next;
      });
      onApplied(r.applied);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApplying(false);
    }
  }

  function applyOne(s: Suggestion) {
    void apply([{ record_id: s.record_id, new_date: s.suggested_date }]);
  }

  function applyAll() {
    // "Apply all" deliberately excludes "unlikely" suggestions and
    // rows that have already been applied or skipped. The user can
    // still apply an unlikely row explicitly via its own Apply
    // button after reviewing the warning.
    const repairs = suggestions
      .filter(
        (s) =>
          !skipped.has(s.record_id) &&
          !applied.has(s.record_id) &&
          s.confidence !== "unlikely",
      )
      .map((s) => ({
        record_id: s.record_id,
        new_date: s.suggested_date,
      }));
    void apply(repairs);
  }

  function toggleSkip(recordId: string) {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) next.delete(recordId);
      else next.add(recordId);
      return next;
    });
  }

  const activeCount = suggestions.filter(
    (s) =>
      !skipped.has(s.record_id) &&
      !applied.has(s.record_id) &&
      s.confidence !== "unlikely",
  ).length;
  const unlikelyCount = suggestions.filter(
    (s) => s.confidence === "unlikely",
  ).length;
  const appliedCount = applied.size;

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="repair-dates-title"
        className="repair-dates-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="repair-dates-header">
          <h2 id="repair-dates-title" className="modal-title">
            Repair record dates
          </h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="repair-dates-intro">
          CaseThread scans each record's body text for a
          correspondence date and compares it to the stored record
          date. Forwarded emails often pick up the forwarding time
          instead of the original send date — these are the records
          that look wrong.
        </p>

        {loading && (
          <div className="repair-dates-loading">
            <span className="spinner" /> Scanning records…
          </div>
        )}

        {error && <div className="form-error">{error}</div>}

        {!loading && suggestions.length === 0 && !error && (
          <div className="repair-dates-empty">
            <p>
              Scanned {scanned} records — no date mismatches found.
              Every record's stored date matches its body content.
            </p>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <>
            <div className="repair-dates-summary">
              Found {suggestions.length} record
              {suggestions.length === 1 ? "" : "s"} with a date that
              differs from the body text.
              {unlikelyCount > 0 && (
                <>
                  {" "}
                  <span className="repair-dates-summary-unlikely">
                    {unlikelyCount} flagged as unlikely — review
                    individually before applying.
                  </span>
                </>
              )}
            </div>

            <ul className="repair-dates-list">
              {suggestions.map((s) => {
                const isSkipped = skipped.has(s.record_id);
                const isApplied = applied.has(s.record_id);
                const isImage =
                  !!s.file_r2_key &&
                  !!s.file_mime_type &&
                  s.file_mime_type.startsWith("image/");
                const imageUrl = isImage
                  ? fileUrl(caseId, s.file_r2_key!)
                  : null;
                const isUnlikely = s.confidence === "unlikely";
                return (
                  <li
                    key={s.record_id}
                    className={`repair-dates-row${
                      isSkipped ? " skipped" : ""
                    }${isApplied ? " applied" : ""}${
                      isUnlikely ? " unlikely" : ""
                    }`}
                  >
                    {imageUrl && (
                      <button
                        type="button"
                        className="repair-dates-row-thumb"
                        onClick={() => setLightboxUrl(imageUrl)}
                        aria-label="Enlarge image"
                        title="Click to enlarge"
                      >
                        <AuthImage src={imageUrl} alt="" loading="lazy" />
                      </button>
                    )}
                    <div className="repair-dates-row-main">
                      <div className="repair-dates-row-dates">
                        <span className="repair-dates-current">
                          {formatDateDisplay(s.current_date)}
                        </span>
                        <span className="repair-dates-arrow">→</span>
                        <span className="repair-dates-suggested">
                          {formatDateDisplay(s.suggested_date + "T12:00:00Z")}
                        </span>
                      </div>
                      {isUnlikely && s.confidence_reason && (
                        <div
                          className="repair-dates-row-warning"
                          role="note"
                        >
                          <span aria-hidden>⚠</span>{" "}
                          {s.confidence_reason}
                        </div>
                      )}
                      {isUnlikely && isImage && (
                        <div
                          className="repair-dates-row-info"
                          role="note"
                        >
                          This record's date reflects when the image
                          was uploaded. For checks and forms, the
                          signature date is usually the most
                          relevant date — check the document for a
                          signed or effective date.
                        </div>
                      )}
                      {s.subject && (
                        <div className="repair-dates-row-subject">
                          {s.subject}
                        </div>
                      )}
                      {s.preview && (
                        <div className="repair-dates-row-preview">
                          {s.preview}
                        </div>
                      )}
                    </div>
                    <div className="repair-dates-row-actions">
                      {isApplied ? (
                        <span
                          className="repair-dates-applied-tag"
                          aria-label="Applied"
                        >
                          Applied ✓
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => applyOne(s)}
                            disabled={applying || isSkipped}
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleSkip(s.record_id)}
                            disabled={applying}
                          >
                            {isSkipped ? "Unskip" : "Skip"}
                          </button>
                        </>
                      )}
                      {onViewRecord && (
                        <button
                          type="button"
                          className="repair-dates-view-link"
                          onClick={() => onViewRecord(s.record_id)}
                          disabled={applying}
                        >
                          View record ↗
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="repair-dates-footer">
              {appliedCount > 0 && (
                <span className="repair-dates-footer-progress">
                  {appliedCount} applied so far
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={applying}
              >
                {appliedCount > 0 ? "Done" : "Close"}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={applyAll}
                disabled={applying || activeCount === 0}
              >
                {applying
                  ? "Applying…"
                  : `Apply all (${activeCount})`}
              </button>
            </div>
          </>
        )}
      </div>

      {lightboxUrl && (
        <ZoomableLightbox
          src={lightboxUrl}
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </ModalBackdrop>
  );
}
